import React, { useEffect, useState, useCallback } from "react";
import { db, auth } from "../../firebase";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import {
    Container, Paper, Typography, Grid, TextField, MenuItem,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Box, CircularProgress, Card, CardContent, Divider
} from "@mui/material";
import { Assessment, Print, FilterList } from "@mui/icons-material";

const PRIMARY = "#0d9488";

function Transportreport() {
    const [loading, setLoading] = useState(true);
    const [schoolId, setSchoolId] = useState("");
    const [mergedStudents, setMergedStudents] = useState([]);
    const [locations, setLocations] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [locationTypes, setLocationTypes] = useState([]);

    // Filters
    const [filterRoute, setFilterRoute] = useState("All");
    const [filterType, setFilterType] = useState("All");

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
            const sId = userSnap.exists() ? userSnap.data().schoolId : "zIlbJM5BjyrlEhg7fWKs";
            setSchoolId(sId);

            const [admSnap, studentSnap, typeSnap, assignSnap, locSnap] = await Promise.all([
                getDocs(query(collection(db, "admission"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "location_types"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "student_assignments"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "locations"), where("schoolId", "==", sId)))
            ]);

            const studentMap = {};
            studentSnap.docs.forEach(d => { studentMap[d.id] = d.data(); });

            setLocationTypes(typeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLocations(locSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            const studentDetails = admSnap.docs.map(doc => {
                const adm = { id: doc.id, ...doc.data() };
                const sData = studentMap[adm.studentId] || {};
                return {
                    id: adm.id,
                    fullName: `${sData.firstname || ""} ${sData.surname || ""}`.trim().toUpperCase(),
                    phone: sData.phone || "N/A"
                };
            });

            setMergedStudents(studentDetails);
            setAssignments(assignSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Report Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filteredData = assignments.filter(row => {
        const matchType = filterType === "All" || row.locationTypeName === filterType;
        const matchRoute = filterRoute === "All" || row.locationId === filterRoute;
        return matchType && matchRoute;
    });

    const totalRevenue = filteredData.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);

    if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ py: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" fontWeight={700} color={PRIMARY} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assessment /> Transport Summary Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Total Assigned: <strong>{filteredData.length}</strong> Students
                </Typography>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ bgcolor: '#f0fdfa', borderLeft: `5px solid ${PRIMARY}` }}>
                        <CardContent>
                            <Typography color="text.secondary" gutterBottom>Total Monthly Collection</Typography>
                            <Typography variant="h4" fontWeight={700} color={PRIMARY}>₹{totalRevenue.toLocaleString()}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2 }} variant="outlined">
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={1}><FilterList color="action" /></Grid>
                            <Grid item xs={5}>
                                <TextField select fullWidth size="small" label="Filter Route Type" value={filterType} onChange={e => setFilterType(e.target.value)}>
                                    <MenuItem value="All">All Types</MenuItem>
                                    {locationTypes.map(t => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField select fullWidth size="small" label="Filter Specific Location" value={filterRoute} onChange={e => setFilterRoute(e.target.value)}>
                                    <MenuItem value="All">All Locations</MenuItem>
                                    {locations.filter(l => filterType === "All" || l.locationType === filterType).map(l => (
                                        <MenuItem key={l.id} value={l.id}>{l.locationName}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {/* Report Table */}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#f8fafc" }}>
                        <TableRow>
                            <TableCell><strong>Sr.</strong></TableCell>
                            <TableCell><strong>Student Name</strong></TableCell>
                            <TableCell><strong>Route Type</strong></TableCell>
                            <TableCell><strong>Location/Pick-up</strong></TableCell>
                            <TableCell align="right"><strong>Monthly Fee</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData.map((row, index) => {
                            const student = mergedStudents.find(s => s.id === row.admissionId);
                            const loc = locations.find(l => l.id === row.locationId);
                            return (
                                <TableRow key={row.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{student?.fullName || "Unknown"}</Typography>
                                        <Typography variant="caption" color="text.secondary">{student?.phone}</Typography>
                                    </TableCell>
                                    <TableCell>{row.locationTypeName}</TableCell>
                                    <TableCell>{loc?.locationName || "N/A"}</TableCell>
                                    <TableCell align="right">₹{row.amount}</TableCell>
                                </TableRow>
                            );
                        })}
                        {filteredData.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>No transport data found for the selected filters</TableCell>
                            </TableRow>
                        )}
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell colSpan={4} align="right"><strong>Total Revenue:</strong></TableCell>
                            <TableCell align="right"><strong>₹{totalRevenue}</strong></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default Transportreport;